import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography
} from '@mui/material'
import { useAuth0 } from '@auth0/auth0-react'
import log from 'loglevel'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { isEmpty, isNil } from 'lodash/fp'
import {
  domainName,
  httpRequest,
  requestGetInvoices
} from '../../functions/common'
import { Loading } from '../Loading'
import { ServerError } from '../ServerError'
import { UserOutletContext, EnumPaymentGmoPgStatus } from '../../types'
import {
  format,
  isWithinInterval,
  setDate,
  startOfMonth,
  subDays
} from 'date-fns/fp'
import { pickMostPriorirtyLog } from './functions'

log.setLevel(log.levels.DEBUG)
const env = process.env['REACT_APP_ENV'] ?? 'dev'

export const PaymentEdit = () => {
  const navigation = useNavigate()
  const { user } = useAuth0()
  const { memberId, subscription } = useOutletContext<UserOutletContext>()
  const [confirm, setConfirm] = useState<boolean>(false)
  const [complete, setComplete] = useState<boolean>(false)
  const [editUrl, setEditUrl] = useState<string | undefined>(undefined)
  const [loading, setLoading] = useState<boolean>(true)
  const [hasError, setHasError] = useState<boolean>(false)
  const [paymentError, setPaymentError] = useState<boolean>(false)
  const [currentPayment, setCurrentPayment] = useState<
    | {
      effectiveDate: string
      amount: number
      status: EnumPaymentGmoPgStatus
    }
    | undefined
  >(undefined)

  useEffect(() => {
    if (!isNil(memberId) && !isNil(subscription?.zuoraAccountId)) {
      const setEditButtonUrl = async () => {
        const regist = await httpRequest(
          'get',
          `https://${domainName}/v1/entry/credit-regist/${memberId}`,
          undefined,
          { Authorization: user?.sub }
        )
        if (isNil(regist)) {
          log.error('[PaymentEdit] cannot get edit credit card url')
          return setHasError(true)
        }
        const { status: registStatus, data: registData } = regist

        if (registStatus === 200 && !isNil(registData)) {
          setEditUrl(registData)
        } else {
          return setHasError(true)
        }
      }
      setEditButtonUrl()
    }
    if (!isNil(user?.sub) && !isNil(subscription)) {
      const start = subDays(1)(startOfMonth(Date.now()))
      const end = setDate(16)(Date.now())
      const getInvoice = async () => {
        const invoices = env === 'dev' ? {
          '2025-08': [{
            effectiveDate: '2025-08-01',
            amount: 550,
            status: EnumPaymentGmoPgStatus.Error
          }],
          '2025-07': [{
            effectiveDate: '2025-07-01',
            amount: 550,
            status: EnumPaymentGmoPgStatus.Processed
          }]
        } : await requestGetInvoices(
          subscription.zuoraAccountNo,
          user?.sub as string
        )
        if (isNil(invoices)) {
          log.error('[PaymentEdit] cannot get invoices')
          setHasError(true)
          setLoading(false)
          return undefined
        }
        const current = pickMostPriorirtyLog(
          invoices[format('yyyy-MM')(Date.now())]
        )
        if (isNil(current)) {
          setLoading(false)
          return undefined
        }
        if (
          isWithinInterval({ start, end })(Date.now()) &&
          current.status === EnumPaymentGmoPgStatus.Error
        ) {
          setPaymentError(true)
          setCurrentPayment(current)
        } else {
          setCurrentPayment(current)
        }
      }
      getInvoice()
    }
  }, [])
  useEffect(() => {
    if (!isNil(editUrl) && !isNil(currentPayment)) setLoading(false)
  }, [editUrl, currentPayment])

  const handleClickEdit = () => {
    if (!isNil(editUrl)) {
      window.open(editUrl, '_blank')
    }
  }
  const handleClickPay = () => {
    setConfirm(true)
  }
  const handleClickDoPay = () => {
    const requestRepayment = async () => {
      setLoading(true)
      const repayment = await httpRequest(
        'post',
        `https://${domainName}/v1/users/payment/repayment/${subscription?.zuoraAccountId}`,
        { targetDate: format('yyyy-MM-dd')(Date.now()) },
        { authorization: user?.sub }
      )
      if (isNil(repayment)) {
        log.error(
          '[common.getEditCreditCardUrl] cannot get edit credit card url'
        )
        setLoading(false)
        return setHasError(true)
      }
      setLoading(false)
    }
    requestRepayment()
    setConfirm(false)
    setComplete(true)
  }
  const handleClickReturn = () => {
    navigation('/user/info')
  }
  const handleCloseConfim = () => {
    setConfirm(false)
  }
  const handleCloseComplete = () => {
    setComplete(false)
  }

  return loading ? (
    <Loading />
  ) : hasError ? (
    <ServerError />
  ) : (
    <Box>
      <Container sx={{ pt: 5 }}>
        <Stack spacing={3}>
          <Typography
            align="left"
            variant="h4"
            sx={{
              borderLeft: 20,
              borderBottom: 1,
              borderColor: 'orange',
              paddingLeft: 1
            }}>
            お支払い方法
          </Typography>
          <Typography variant="caption" mt={5}>
            クレジットカードの登録は【再登録】ボタンからお進みください。
          </Typography>
          <Typography variant="caption">
            ※決済代行会社のサイトに遷移します。
          </Typography>
          <Typography variant="caption">
          ※テラシテでは、カード情報の不正使用を未然に防止するため、3Dセキュア2.0を導入いたしました。<br />
          つきましては、3Dセキュア2.0のサービス登録設定済のクレジットカードをご利用ください。<br />
          クレジットカードの3Dセキュア2.0の設定方法につきましては、ご利用のカード発行会社のWebサイトなどでご確認ください。<br />
          </Typography>
          
        </Stack>
        <Box display={'flex'} justifyContent={'space-around'} paddingTop={3}>
          <Box justifyContent={'center'}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleClickEdit}>
              再登録
            </Button>
          </Box>
        </Box>
        {paymentError ? (
          <>
            <Typography variant="caption" mt={5}>
              有効なカードが登録された状態で【再決済】ボタンを押下いただくと、当月からご利用を再開いただけます。
            </Typography>
            <Box
              display={'flex'}
              justifyContent={'space-around'}
              paddingTop={3}>
              <Box justifyContent={'center'}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleClickPay}>
                  再決済
                </Button>
              </Box>
            </Box>
            <Typography variant="caption" mt={5}>
              ※お客さまは当月の決済がエラーとなっており、お支払い手続きが完了しておりません。つきましては、本サービスのご利用を一時停止させていただいております。
              <br />
              翌月1日のお支払いが完了すると翌月からサービスを再開いたしますが、
              <Typography variant="caption" color="red">
                当月の途中からサービスのご利用再開をご希望の方は、ご利用可能なカードを上の【再登録】ポタンから再登録後、【再決済】ボタンを押し再決済を行ってください。
              </Typography>
              <br />
              ※再決済は当月15日まで実施可能です。
              <br />
              ※再決済でのお支払い完了後、サービスがご利用可能となるまで3営業日程度かかる場合がございます。
            </Typography>
          </>
        ) : (
          <></>
        )}
        <Box display={'flex'} justifyContent={'space-around'} paddingTop={3}>
          <Box justifyContent={'center'}>
            <Button
              variant="contained"
              color="inherit"
              onClick={handleClickReturn}>
              戻る
            </Button>
          </Box>
        </Box>
        <Dialog open={confirm} onClose={handleCloseConfim}>
          <DialogTitle>再決済実施確認</DialogTitle>
          <DialogContent>
            <DialogContentText>
              有効なクレジットカード情報を再登録いただけましたか？
            </DialogContentText>
            <DialogActions>
              <Button color="inherit" onClick={handleCloseConfim}>
                キャンセル
              </Button>
              <Button type="submit" color="primary" onClick={handleClickDoPay}>
                再決済を行う
              </Button>
            </DialogActions>
          </DialogContent>
        </Dialog>
        <Dialog open={complete} onClose={handleCloseComplete}>
          <DialogTitle>再決済受付完了</DialogTitle>
          <DialogContent>
            <DialogContentText>
              再決済を受付けました。
              <br />
              ※「決済完了」が表示されるまでにはお時間がかかります。
            </DialogContentText>
            <DialogActions>
              <Button color="primary" onClick={handleCloseComplete}>
                OK
              </Button>
            </DialogActions>
          </DialogContent>
        </Dialog>
      </Container>
    </Box>
  )
}